import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PII_MASK_CLASS } from "@/lib/analytics";
import {
  countIncluded,
  countUnresolvedAmbiguous,
  isResolved,
  type VoiceReviewState
} from "@/lib/voice-order";
import { convertToLocale } from "@/utils/money";
import type { StoreVoiceOrderItem } from "@repo/api-sdk";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { HelpCircle, Search, ShoppingCart } from "lucide-react";

import { VoiceQuantityStepper } from "./VoiceQuantityStepper";

interface MatchReviewStageProps {
  transcript: string;
  items: StoreVoiceOrderItem[];
  state: VoiceReviewState;
  currencyCode: string;
  isResolving: boolean;
  error: string | null;
  onSelectOption: (index: number, variantId: string) => void;
  onClearOption: (index: number) => void;
  onQuantityChange: (index: number, quantity: number) => void;
  onToggleInclude: (index: number, included: boolean) => void;
  onSearch: (query: string) => void;
  onCancel: () => void;
  onContinue: () => void;
}

/**
 * Stage 1 (match review): the flat, seller-less parse result. The buyer fixes
 * quantities on matched items, picks the right product for ambiguous ones, and
 * gets a manual catalog search for anything not found - before we resolve live
 * sellers and prices (stage 2). Spoken text is masked from session replay
 * (ADR-0006): it is free-form and could contain more than product names.
 */
export function MatchReviewStage({
  transcript,
  items,
  state,
  currencyCode,
  isResolving,
  error,
  onSelectOption,
  onClearOption,
  onQuantityChange,
  onToggleInclude,
  onSearch,
  onCancel,
  onContinue
}: MatchReviewStageProps) {
  const includedCount = countIncluded(state);
  const unresolvedAmbiguous = countUnresolvedAmbiguous(items, state);
  const fmt = (amount: number) =>
    convertToLocale({ amount, currency_code: currencyCode });

  return (
    <>
      {transcript ? (
        <div className="border-border border-b px-6 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            You said
          </p>
          <p
            className={cn(
              "mt-0.5 text-sm italic text-gray-600 dark:text-gray-300",
              PII_MASK_CLASS
            )}
          >
            “{transcript}”
          </p>
        </div>
      ) : null}

      <ul className="divide-border max-h-[46vh] divide-y overflow-y-auto px-6">
        {items.map((item, index) => (
          <MatchItemRow
            key={`${item.match_type}-${index}`}
            item={item}
            index={index}
            state={state}
            fmt={fmt}
            onSelectOption={onSelectOption}
            onClearOption={onClearOption}
            onQuantityChange={onQuantityChange}
            onToggleInclude={onToggleInclude}
            onSearch={onSearch}
          />
        ))}
      </ul>

      {unresolvedAmbiguous > 0 ? (
        <p className="mx-6 mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
          {unresolvedAmbiguous} {unresolvedAmbiguous === 1 ? "item" : "items"}{" "}
          still {unresolvedAmbiguous === 1 ? "needs" : "need"} a choice -
          unresolved items are left out.
        </p>
      ) : null}

      {error ? (
        <p
          role="alert"
          className="mx-6 mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
        >
          {error}
        </p>
      ) : null}

      <footer className="border-border flex flex-col-reverse gap-2 border-t p-6 pt-4 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Start over
        </Button>
        <Button
          type="button"
          onClick={onContinue}
          disabled={includedCount === 0 || isResolving}
        >
          <ShoppingCart className="mr-2 h-4 w-4" aria-hidden="true" />
          {isResolving
            ? "Checking prices…"
            : `Continue with ${includedCount} ${includedCount === 1 ? "item" : "items"}`}
        </Button>
      </footer>
    </>
  );
}

function MatchItemRow({
  item,
  index,
  state,
  fmt,
  onSelectOption,
  onClearOption,
  onQuantityChange,
  onToggleInclude,
  onSearch
}: {
  item: StoreVoiceOrderItem;
  index: number;
  state: VoiceReviewState;
  fmt: (amount: number) => string;
  onSelectOption: (index: number, variantId: string) => void;
  onClearOption: (index: number) => void;
  onQuantityChange: (index: number, quantity: number) => void;
  onToggleInclude: (index: number, included: boolean) => void;
  onSearch: (query: string) => void;
}) {
  const itemState = state[index];

  if (item.match_type === "not_found") {
    return (
      <li className="flex items-start justify-between gap-3 py-3">
        <div className="min-w-0">
          <TruncateTooltip
            as="p"
            className="text-sm font-medium text-gray-500 line-through dark:text-gray-400"
          >
            <span className={PII_MASK_CLASS}>{item.spoken_name}</span>
          </TruncateTooltip>
          <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-neutral-800 dark:text-gray-400">
            Not found
          </span>
        </div>
        <button
          type="button"
          onClick={() => onSearch(item.spoken_name)}
          className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium text-purple-600 hover:underline dark:text-purple-400"
        >
          <Search className="h-3 w-3" aria-hidden="true" />
          Search catalog
        </button>
      </li>
    );
  }

  // Ambiguous and not yet picked: show the option picker.
  if (item.match_type === "ambiguous" && !itemState?.variantId) {
    return (
      <li className="py-3">
        <div className="flex items-center gap-2">
          <HelpCircle
            className="h-4 w-4 flex-shrink-0 text-amber-500"
            aria-hidden="true"
          />
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Which one did you mean by{" "}
            <span className={cn("font-medium", PII_MASK_CLASS)}>
              “{item.spoken_name}”
            </span>
            ?
          </p>
        </div>
        <ul className="mt-2 space-y-1.5">
          {item.options.map((option) => (
            <li key={option.variant_id}>
              <button
                type="button"
                onClick={() => onSelectOption(index, option.variant_id)}
                className="border-border flex w-full items-center rounded-md border px-3 py-2 text-left text-sm text-gray-800 transition-colors hover:border-purple-400 hover:bg-purple-50 dark:text-gray-100 dark:hover:bg-purple-900/20"
              >
                {option.title}
              </button>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  // Resolved line (exact match, or a picked ambiguous option). A picked variant
  // always resolves back to one of the options, so the fallback is unreachable -
  // but keep it a neutral, non-PII label rather than the raw spoken phrase,
  // which is masked everywhere else it appears (ADR-0006) and would leak here
  // via the title, the aria-label, and the stepper label.
  const title =
    item.match_type === "exact"
      ? item.resolved_product.title
      : (item.options.find((o) => o.variant_id === itemState?.variantId)
          ?.title ?? "Selected item");
  const indicativePrice =
    item.match_type === "exact" ? item.resolved_product.indicative_price : null;
  const checked = Boolean(itemState?.included && isResolved(itemState));

  return (
    <li className={cn("flex gap-3 py-3", !checked && "opacity-50")}>
      <Checkbox
        checked={checked}
        onCheckedChange={(value) => onToggleInclude(index, value === true)}
        aria-label={`Include ${title}`}
        className="mt-1 flex-shrink-0"
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <TruncateTooltip
            as="p"
            className="text-sm font-medium text-gray-900 dark:text-white"
          >
            {title}
          </TruncateTooltip>
          {indicativePrice != null ? (
            <span className="flex-shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
              ≈ {fmt(indicativePrice)}
              <span className="block text-[10px] uppercase tracking-wide text-gray-400">
                indicative
              </span>
            </span>
          ) : null}
        </div>
        <div className="mt-1.5 flex items-center gap-3">
          <VoiceQuantityStepper
            quantity={itemState?.quantity ?? 1}
            disabled={!checked}
            onChange={(quantity) => onQuantityChange(index, quantity)}
            label={title}
          />
          {item.match_type === "ambiguous" ? (
            <button
              type="button"
              onClick={() => onClearOption(index)}
              className="text-xs font-medium text-purple-600 hover:underline dark:text-purple-400"
            >
              Change
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}
